import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { 
  Shield, Smartphone, Monitor, LogOut, Key, Mail, MessageSquare, 
  QrCode, RefreshCw, Copy, CheckCircle2, AlertTriangle, Loader2,
  Eye, EyeOff, Globe, Clock
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { getFriendlyError } from '../../utils/friendlyErrors';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VendorSecurity = () => {
  const { apiCall, token, user } = useAuth();
  const [loading, setLoading] = useState(false);
  
  // Password change state
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // 2FA Status state
  const [twoFAStatus, setTwoFAStatus] = useState(null);
  const [twoFALoading, setTwoFALoading] = useState(true);
  
  // TOTP Setup state
  const [totpSetupDialog, setTotpSetupDialog] = useState(false);
  const [totpSetupData, setTotpSetupData] = useState(null);
  const [totpVerifyCode, setTotpVerifyCode] = useState('');
  const [totpSetupLoading, setTotpSetupLoading] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null);
  
  // Session management state
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  
  // OTP Testing state
  const [otpTestDialog, setOtpTestDialog] = useState({ open: false, method: null });
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);

  // Fetch 2FA status
  const fetchTwoFAStatus = useCallback(async () => {
    setTwoFALoading(true);
    try {
      const response = await fetch(`${API_URL}/api/2fa/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTwoFAStatus(data);
      }
    } catch (error) {
      console.error('Error fetching 2FA status:', error);
    } finally {
      setTwoFALoading(false);
    }
  }, [token]);

  // Fetch sessions
  const fetchSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/sessions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSessions(data.sessions || []);
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      setSessionsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchTwoFAStatus();
    fetchSessions();
  }, [fetchTwoFAStatus, fetchSessions]);

  // Password change handler
  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordData.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      await apiCall('/api/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({
          current_password: passwordData.current_password,
          new_password: passwordData.new_password
        })
      });
      toast.success('Password changed successfully');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(getFriendlyError(error, 'Unable to change password. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  // TOTP Setup
  const initiateToTPSetup = async () => {
    setTotpSetupLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/2fa/setup`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTotpSetupData(data);
        setTotpSetupDialog(true);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Unable to set up two-factor authentication. Please try again.');
      }
    } catch (error) {
      toast.error('Unable to initiate 2FA setup. Please try again.');
    } finally {
      setTotpSetupLoading(false);
    }
  };

  const verifyTOTPSetup = async () => {
    if (!totpVerifyCode || totpVerifyCode.length !== 6) {
      toast.error('Please enter a 6-digit verification code.');
      return;
    }

    setTotpSetupLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/2fa/verify-setup`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ otp_code: totpVerifyCode })
      });
      
      if (response.ok) {
        const data = await response.json();
        setBackupCodes(data.backup_codes);
        toast.success('2FA enabled successfully!');
        fetchTwoFAStatus();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'The code you entered is incorrect. Please try again.');
      }
    } catch (error) {
      toast.error('Verification failed. Please try again.');
    } finally {
      setTotpSetupLoading(false);
    }
  };

  const disableTOTP = async () => {
    if (!window.confirm('Are you sure you want to disable 2FA? This will reduce your account security.')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/2fa/disable`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('2FA disabled');
        fetchTwoFAStatus();
        setTotpSetupDialog(false);
        setBackupCodes(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Unable to disable 2FA. Please try again.');
      }
    } catch (error) {
      toast.error('Unable to disable 2FA. Please try again.');
    }
  };

  // Send OTP (Email or SMS)
  const sendOTP = async (method) => {
    setOtpLoading(true);
    try {
      const endpoint = method === 'email' 
        ? `${API_URL}/api/otp/email/send`
        : `${API_URL}/api/otp/send`;
      
      const body = method === 'email'
        ? { email: user?.email, name: user?.name, purpose: '2fa_test' }
        : { mobile: user?.mobile, purpose: '2fa_test' };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      
      if (response.ok) {
        setOtpSent(true);
        toast.success(`Verification code sent via ${method}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Unable to send verification code. Please try again.');
      }
    } catch (error) {
      toast.error('Unable to send verification code. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  const verifyOTP = async () => {
    if (!otpCode || otpCode.length < 4) {
      toast.error('Please enter the verification code sent to you.');
      return;
    }

    setOtpLoading(true);
    try {
      const endpoint = otpTestDialog.method === 'email'
        ? `${API_URL}/api/otp/email/verify`
        : `${API_URL}/api/otp/verify`;
      
      const body = otpTestDialog.method === 'email'
        ? { email: user?.email, otp: otpCode }
        : { mobile: user?.mobile, otp: otpCode };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      
      if (response.ok) {
        toast.success('OTP verified successfully!');
        setOtpTestDialog({ open: false, method: null });
        setOtpCode('');
        setOtpSent(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || error.message || 'The code you entered is incorrect. Please check and try again.');
      }
    } catch (error) {
      toast.error('Verification failed. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  // Terminate session
  const terminateSession = async (sessionId) => {
    try {
      const response = await fetch(`${API_URL}/api/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Session ended');
        fetchSessions();
      } else {
        toast.error('Unable to end this session. Please try again.');
      }
    } catch (error) {
      toast.error('Unable to end session. Please try again.');
    }
  };

  const terminateAllSessions = async () => {
    if (!window.confirm('This will log you out of all devices except this one. Continue?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/sessions`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('All other sessions ended');
        fetchSessions();
      } else {
        toast.error('Unable to end all sessions. Please try again.');
      }
    } catch (error) {
      toast.error('Unable to end sessions. Please try again.');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
            <Shield className="h-7 w-7" />
            Security Settings
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage your account security, passwords, and authentication methods
          </p>
        </div>

        <Tabs defaultValue="authentication" className="space-y-4 md:space-y-6">
          <div className="overflow-x-auto -mx-2 px-2">
            <TabsList className="inline-flex w-auto">
              <TabsTrigger value="authentication" className="text-xs md:text-sm">Authentication</TabsTrigger>
              <TabsTrigger value="sessions" className="text-xs md:text-sm">Active Sessions</TabsTrigger>
            </TabsList>
          </div>

          {/* Authentication Tab */}
          <TabsContent value="authentication" className="space-y-6">
            {/* Password Change */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Key className="h-5 w-5" />
                  Change Password
                </CardTitle>
                <CardDescription>
                  Update your password regularly for better security
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
                  <div>
                    <Label>Current Password</Label>
                    <div className="relative">
                      <Input
                        type={showPasswords.current ? 'text' : 'password'}
                        value={passwordData.current_password}
                        onChange={(e) => setPasswordData(prev => ({ ...prev, current_password: e.target.value }))}
                        required
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3"
                        onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                      >
                        {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Label>New Password</Label>
                    <div className="relative">
                      <Input
                        type={showPasswords.new ? 'text' : 'password'}
                        value={passwordData.new_password}
                        onChange={(e) => setPasswordData(prev => ({ ...prev, new_password: e.target.value }))}
                        required
                        minLength={8}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3"
                        onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                      >
                        {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Label>Confirm New Password</Label>
                    <div className="relative">
                      <Input
                        type={showPasswords.confirm ? 'text' : 'password'}
                        value={passwordData.confirm_password}
                        onChange={(e) => setPasswordData(prev => ({ ...prev, confirm_password: e.target.value }))}
                        required
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3"
                        onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                      >
                        {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                  <Button type="submit" disabled={loading}>
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Update Password
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Two-Factor Authentication */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Two-Factor Authentication (2FA)
                </CardTitle>
                <CardDescription>
                  Add an extra layer of security to your account
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {twoFALoading ? (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading security status...
                  </div>
                ) : (
                  <>
                    {/* Available Methods */}
                    <div className="space-y-4">
                      <h4 className="font-medium">Available Methods</h4>
                      
                      {twoFAStatus?.available_methods?.map((method) => (
                        <div 
                          key={method.type}
                          className="flex items-center justify-between p-4 border rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            {method.type === 'email' && <Mail className="h-5 w-5 text-blue-500" />}
                            {method.type === 'sms' && <MessageSquare className="h-5 w-5 text-green-500" />}
                            {method.type === 'totp' && <QrCode className="h-5 w-5 text-purple-500" />}
                            <div>
                              <p className="font-medium">{method.label}</p>
                              {method.destination && (
                                <p className="text-sm text-muted-foreground">{method.destination}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {method.enabled ? (
                              <Badge className="bg-green-100 text-green-700">Enabled</Badge>
                            ) : (
                              <Badge variant="outline">Not Set Up</Badge>
                            )}
                            {method.type === 'totp' ? (
                              method.enabled ? (
                                <Button variant="outline" size="sm" onClick={disableTOTP}>
                                  Disable
                                </Button>
                              ) : (
                                <Button 
                                  size="sm" 
                                  onClick={initiateToTPSetup}
                                  disabled={totpSetupLoading}
                                >
                                  {totpSetupLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Set Up'}
                                </Button>
                              )
                            ) : method.type !== 'totp' && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => {
                                  setOtpTestDialog({ open: true, method: method.type });
                                  setOtpSent(false);
                                  setOtpCode('');
                                }}
                              >
                                Test
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Backup Codes */}
                    {twoFAStatus?.totp_enabled && (
                      <div className="p-4 bg-muted rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">Backup Codes</p>
                            <p className="text-sm text-muted-foreground">
                              {twoFAStatus.backup_codes_remaining} codes remaining
                            </p>
                          </div>
                          <Button variant="outline" size="sm" onClick={async () => {
                            try {
                              const response = await fetch(`${API_URL}/api/2fa/backup-codes`, {
                                method: 'POST',
                                headers: { 'Authorization': `Bearer ${token}` }
                              });
                              if (response.ok) {
                                const data = await response.json();
                                setBackupCodes(data.backup_codes);
                                setTotpSetupDialog(true);
                              }
                            } catch (error) {
                              toast.error('Unable to generate new backup codes. Please try again.');
                            }
                          }}>
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Regenerate
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Sessions Tab */}
          <TabsContent value="sessions" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Active Sessions</CardTitle>
                    <CardDescription>
                      Devices currently logged into your account
                    </CardDescription>
                  </div>
                  <Button variant="destructive" size="sm" onClick={terminateAllSessions}>
                    <LogOut className="h-4 w-4 mr-2" />
                    Log Out All
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {sessionsLoading ? (
                  <div className="flex items-center gap-2 text-muted-foreground py-8 justify-center">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading sessions...
                  </div>
                ) : sessions.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">
                    No active sessions found
                  </p>
                ) : (
                  <div className="space-y-4">
                    {sessions.map((session) => (
                      <div 
                        key={session.session_id}
                        className={`flex items-center justify-between p-4 border rounded-lg ${
                          session.is_current ? 'border-green-200 bg-green-50' : ''
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="p-2 bg-muted rounded-lg">
                            {session.device_type === 'mobile' ? (
                              <Smartphone className="h-5 w-5" />
                            ) : (
                              <Monitor className="h-5 w-5" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium flex items-center gap-2">
                              {session.user_agent?.split(' ')[0] || 'Unknown Device'}
                              {session.is_current && (
                                <Badge variant="outline" className="text-green-600 border-green-300">
                                  Current
                                </Badge>
                              )}
                            </p>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Globe className="h-3 w-3" />
                                {session.ip_address || 'Unknown IP'}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {session.last_active ? new Date(session.last_active).toLocaleString() : 'Active'}
                              </span>
                            </div>
                          </div>
                        </div>
                        {!session.is_current && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => terminateSession(session.session_id)}
                          >
                            <LogOut className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* TOTP Setup Dialog */}
      <Dialog open={totpSetupDialog} onOpenChange={setTotpSetupDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {backupCodes ? 'Save Your Backup Codes' : 'Set Up Authenticator App'}
            </DialogTitle>
            <DialogDescription>
              {backupCodes 
                ? 'Store these codes in a safe place. You can use them if you lose access to your authenticator app.'
                : 'Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)'}
            </DialogDescription>
          </DialogHeader>
          
          {backupCodes ? (
            <div className="space-y-4">
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  These codes will only be shown once. Save them now!
                </AlertDescription>
              </Alert>
              <div className="grid grid-cols-2 gap-2 p-4 bg-muted rounded-lg font-mono text-sm">
                {backupCodes.map((code, idx) => (
                  <div key={code.id || code.name || `${code.title || "code"}-${idx}`} className="p-2 bg-background rounded">{code}</div>
                ))}
              </div>
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => copyToClipboard(backupCodes.join('\n'))}
              >
                <Copy className="h-4 w-4 mr-2" />
                Copy All Codes
              </Button>
              <Button className="w-full" onClick={() => {
                setTotpSetupDialog(false);
                setBackupCodes(null);
                setTotpSetupData(null);
                setTotpVerifyCode('');
              }}>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                I've Saved My Codes
              </Button>
            </div>
          ) : totpSetupData ? (
            <div className="space-y-4">
              {/* QR Code */}
              <div className="flex justify-center p-4 bg-white rounded-lg">
                <img 
                  src={totpSetupData.qr_code} 
                  alt="2FA QR Code" 
                  className="w-48 h-48"
                />
              </div>
              
              {/* Manual Entry */}
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Can't scan? Enter this code manually:</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-sm font-mono break-all">{totpSetupData.secret}</code>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => copyToClipboard(totpSetupData.secret)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              {/* Verification */}
              <div>
                <Label>Enter the 6-digit code from your app</Label>
                <Input
                  type="text"
                  maxLength={6}
                  placeholder="000000"
                  value={totpVerifyCode}
                  onChange={(e) => setTotpVerifyCode(e.target.value.replace(/\D/g, ''))}
                  className="text-center text-2xl tracking-widest font-mono mt-2"
                />
              </div>
              
              <Button 
                className="w-full" 
                onClick={verifyTOTPSetup}
                disabled={totpSetupLoading || totpVerifyCode.length !== 6}
              >
                {totpSetupLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Verify & Enable 2FA
              </Button>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* OTP Test Dialog */}
      <Dialog open={otpTestDialog.open} onOpenChange={(open) => {
        setOtpTestDialog({ ...otpTestDialog, open });
        if (!open) {
          setOtpSent(false);
          setOtpCode('');
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Test {otpTestDialog.method === 'email' ? 'Email' : 'SMS'} OTP
            </DialogTitle>
            <DialogDescription>
              {otpSent 
                ? `Enter the OTP sent to your ${otpTestDialog.method === 'email' ? 'email' : 'phone'}`
                : `We'll send a test OTP to verify your ${otpTestDialog.method === 'email' ? 'email' : 'phone number'}`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {!otpSent ? (
              <Button 
                className="w-full" 
                onClick={() => sendOTP(otpTestDialog.method)}
                disabled={otpLoading}
              >
                {otpLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Send OTP
              </Button>
            ) : (
              <>
                <div>
                  <Label>Enter OTP</Label>
                  <Input
                    type="text"
                    maxLength={6}
                    placeholder="Enter OTP"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value)}
                    className="text-center text-xl tracking-widest font-mono mt-2"
                  />
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => sendOTP(otpTestDialog.method)}
                    disabled={otpLoading}
                  >
                    Resend
                  </Button>
                  <Button 
                    className="flex-1"
                    onClick={verifyOTP}
                    disabled={otpLoading || otpCode.length < 4}
                  >
                    {otpLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Verify
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default VendorSecurity;
